import { Users, Music, Headphones, CreditCard, DollarSign, TrendingUp, Repeat, BarChart3, UserPlus, ArrowRight, ArrowDown, Clock, Gift, Calendar as CalendarIcon, Target, AlertTriangle, PartyPopper, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAnalyticsDashboard, useDateRange, type DatePreset } from "@/hooks/useAnalyticsDashboard";
import { useRefundStats } from "@/hooks/useAdminData";
import { cn } from "@/lib/utils";

type DashData = NonNullable<ReturnType<typeof useAnalyticsDashboard>["data"]>;

const presets: { value: DatePreset; label: string }[] = [
  { value: "today", label: "Сегодня" },
  { value: "yesterday", label: "Вчера" },
  { value: "7days", label: "7 дней" },
  { value: "30days", label: "30 дней" },
];

export default function AnalyticsDashboard() {
  const { preset, setPreset, range, customRange, setCustomRange } = useDateRange();
  const { data, isLoading } = useAnalyticsDashboard(range);
  const { data: refundStats, isLoading: refundLoading } = useRefundStats();

  const handleCustomDate = (type: "from" | "to", date: Date | undefined) => {
    if (!date) return;
    const current = customRange || { from: new Date(), to: new Date() };
    const updated = type === "from" ? { ...current, from: date } : { ...current, to: date };
    setCustomRange(updated);
    setPreset("custom");
  };

  return (
    <AdminLayout title="📊 Дашборд аналитики" description="Ключевые метрики, воронка и продвинутая аналитика">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {presets.map(p => (
          <Button key={p.value} variant={preset === p.value ? "default" : "secondary"} size="sm" onClick={() => setPreset(p.value)}>
            {p.label}
          </Button>
        ))}
        <div className="flex items-center gap-1">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant={preset === "custom" ? "default" : "outline"} size="sm" className="text-xs">
                <CalendarIcon className="w-3.5 h-3.5 mr-1" />
                {preset === "custom" && customRange ? format(customRange.from, "dd.MM.yy", { locale: ru }) : "От"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={customRange?.from}
                onSelect={(d) => handleCustomDate("from", d)}
                locale={ru}
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
          <span className="text-xs text-muted-foreground">—</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant={preset === "custom" ? "default" : "outline"} size="sm" className="text-xs">
                <CalendarIcon className="w-3.5 h-3.5 mr-1" />
                {preset === "custom" && customRange ? format(customRange.to, "dd.MM.yy", { locale: ru }) : "До"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={customRange?.to}
                onSelect={(d) => handleCustomDate("to", d)}
                locale={ru}
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {isLoading || !data ? (
        <div className="space-y-4">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div>
      ) : (
        <div className="space-y-8">
          <KPIHero data={data} />
          <PeriodComparisonBlock data={data.periodComparison} />
          <SummaryCards data={data.summary} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ConversionKPIBlock data={data.conversionKPI} />
            <TimeToPurchaseBlock data={data.timeToPurchase} />
          </div>

          <FunnelBlock steps={data.funnel} loss={data.funnelLoss} />
          <SpecialOffer99Block data={data.specialOffer99} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PackagesBlock packages={data.packages} />
            <LTVBlock ltv={data.ltv} avgHitsPerPayer={data.avgHitsPerPayer} />
          </div>

          <UTMBlock rows={data.utmRows} />
          <NewUserBlock data={data.newUserBehavior} />
          <PartyBlock data={data.partyAnalysis} />
          <CohortBlock cohorts={data.cohorts} />
          <ChartsBlock dailyData={data.dailyData} />
          <RefundBlock data={refundStats} isLoading={refundLoading} />
        </div>
      )}
    </AdminLayout>
  );
}

// --- KPI Hero ---
function KPIHero({ data }: { data: DashData }) {
  const convPct = data.summary.gotDemo > 0 ? Math.round((data.summary.paid / data.summary.gotDemo) * 100) : 0;
  const convColor = convPct >= 30 ? "text-green-500" : convPct >= 20 ? "text-yellow-500" : "text-destructive";

  const kpis = [
    { label: "Конверсия демо→покупка", value: `${convPct}%`, colorClass: convColor },
    { label: "Средний чек", value: `${data.summary.avgCheck} ₽` },
    { label: "Выручка за период", value: `${data.summary.totalRevenue.toLocaleString()} ₽` },
    { label: "Повторные покупки", value: `${data.summary.repeatPurchasePercent}%` },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {kpis.map(kpi => (
        <Card key={kpi.label} className="bg-primary/5 border-primary/20">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">{kpi.label}</p>
            <span className={cn("text-2xl font-bold", kpi.colorClass)}>{kpi.value}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// --- Period Comparison ---
function PeriodComparisonBlock({ data }: { data: DashData["periodComparison"] }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {data.map(item => (
        <Card key={item.label} className={cn("bg-card border-border", item.isNegative && Math.abs(item.delta) > 15 && "border-destructive/40")}>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">{item.label}</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-lg font-bold">{typeof item.current === "number" && item.label === "Выручка" ? `${item.current.toLocaleString()} ₽` : item.current}</span>
              <span className={cn("text-xs font-medium", item.isNegative ? "text-destructive" : "text-green-500")}>
                {item.delta > 0 ? "+" : ""}{item.delta}%
              </span>
            </div>
            <p className="text-[10px] text-muted-foreground/60">пред: {item.label === "Выручка" ? `${item.previous.toLocaleString()} ₽` : item.previous}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// --- Summary ---
function SummaryCards({ data }: { data: DashData["summary"] }) {
  const cards = [
    { label: "Уникальные пользователи", value: data.uniqueUsers, emoji: "👥" },
    { label: "Начали создание", value: data.startedCreation, emoji: "🎵" },
    { label: "Получили демо", value: data.gotDemo, emoji: "🎧" },
    { label: "Оплатили", value: data.paid, emoji: "💳" },
    { label: "Выручка", value: `${data.totalRevenue.toLocaleString()} ₽`, emoji: "💰" },
    { label: "Ср. чек", value: `${data.avgCheck} ₽`, emoji: "📈" },
    { label: "Повторные", value: `${data.repeatPurchasePercent}%`, emoji: "🔁" },
  ];
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
      {cards.map(c => (
        <Card key={c.label} className="bg-card border-border">
          <CardContent className="p-2.5">
            <p className="text-[10px] text-muted-foreground">{c.emoji} {c.label}</p>
            <p className="text-base font-bold mt-0.5">{c.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// --- 1. Conversion KPI ---
function ConversionKPIBlock({ data }: { data: DashData["conversionKPI"] }) {
  const color = (v: number) => v >= 30 ? "text-green-500" : v >= 20 ? "text-yellow-500" : "text-destructive";
  const bg = (v: number) => v >= 30 ? "bg-green-500/10" : v >= 20 ? "bg-yellow-500/10" : "bg-destructive/10";

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Target className="w-5 h-5 text-primary" />
          🎯 Конверсия демо → покупка
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: "За период", value: data.current },
            { label: "Сегодня", value: data.today },
            { label: "7 дней", value: data.week7 },
            { label: "30 дней", value: data.week30 },
          ].map(item => (
            <div key={item.label} className={cn("p-3 rounded-lg text-center", bg(item.value))}>
              <p className="text-xs text-muted-foreground">{item.label}</p>
              <p className={cn("text-xl font-bold", color(item.value))}>{item.value}%</p>
            </div>
          ))}
        </div>
        <div className="mt-3 p-2 rounded bg-muted/50 text-center">
          <span className="text-xs text-muted-foreground">vs пред. период: </span>
          <span className={cn("text-sm font-bold", data.delta >= 0 ? "text-green-500" : "text-destructive")}>
            {data.delta > 0 ? "+" : ""}{data.delta}pp
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// --- 2. Time to Purchase ---
function TimeToPurchaseBlock({ data }: { data: DashData["timeToPurchase"] }) {
  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="w-5 h-5 text-primary" />
          ⏱ Время до покупки
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-muted/50 text-center">
            <p className="text-xs text-muted-foreground">Среднее</p>
            <p className="text-xl font-bold">{data.avgMinutes} мин</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/50 text-center">
            <p className="text-xs text-muted-foreground">Медиана</p>
            <p className="text-xl font-bold">{data.medianMinutes} мин</p>
          </div>
          <div className="p-3 rounded-lg bg-green-500/10 text-center">
            <p className="text-xs text-muted-foreground">В первые 5 мин</p>
            <p className="text-xl font-bold text-green-500">{data.within5min}%</p>
          </div>
          <div className="p-3 rounded-lg bg-destructive/10 text-center">
            <p className="text-xs text-muted-foreground">После 10 мин</p>
            <p className="text-xl font-bold text-destructive">{data.after10min}%</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Funnel with Loss ---
function FunnelBlock({ steps, loss }: { steps: DashData["funnel"]; loss: DashData["funnelLoss"] }) {
  const maxValue = Math.max(...steps.map(s => s.value), 1);
  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" />
          Воронка конверсии
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {steps.map((step, i) => (
            <div key={step.label}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium">{step.label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold">{step.value}</span>
                  {i > 0 && <Badge variant="outline" className="text-xs">{step.percent}%</Badge>}
                </div>
              </div>
              <div className="h-5 bg-muted rounded-md overflow-hidden">
                <div className={cn("h-full rounded-md transition-all duration-500", i === 0 ? "bg-primary" : i === 1 ? "bg-primary/80" : i === 2 ? "bg-primary/60" : "bg-primary/40")}
                  style={{ width: `${Math.max((step.value / maxValue) * 100, 2)}%` }} />
              </div>
              {i < steps.length - 1 && <div className="flex justify-center my-0.5"><ArrowDown className="w-3 h-3 text-muted-foreground" /></div>}
            </div>
          ))}
        </div>
        {loss.percentLoss > 0 && (
          <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              <span className="text-sm font-medium">📉 Самая большая потеря</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {loss.fromStep} → {loss.toStep}: <span className="font-bold text-destructive">−{loss.absoluteDrop}</span> ({loss.percentLoss}% потерь)
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- 3. Special Offer 99₽ ---
function SpecialOffer99Block({ data }: { data: DashData["specialOffer99"] }) {
  const steps = [
    { label: "Увидели предложение", value: data.shown, emoji: "👀" },
    { label: "Купили за 99 ₽", value: data.bought, emoji: "💳" },
    { label: "Конверсия", value: `${data.conversion}%`, emoji: "🎯" },
    { label: "Повторная покупка", value: `${data.repeatPurchase}%`, emoji: "🔁" },
  ];

  return (
    <Card className="bg-card border-border border-yellow-500/20">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Gift className="w-5 h-5 text-yellow-500" />
          🎁 Спецпредложение 99 ₽
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {steps.map(s => (
            <div key={s.label} className="p-3 rounded-lg bg-yellow-500/5 text-center">
              <p className="text-xs text-muted-foreground">{s.emoji} {s.label}</p>
              <p className="text-xl font-bold mt-1">{s.value}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// --- Packages with Repeat Rate ---
function PackagesBlock({ packages }: { packages: DashData["packages"] }) {
  return (
    <Card className="bg-card border-border">
      <CardHeader><CardTitle>📦 Пакеты</CardTitle></CardHeader>
      <CardContent>
        {packages.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Нет продаж</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Пакет</TableHead>
                <TableHead className="text-right">Продажи</TableHead>
                <TableHead className="text-right">Выручка</TableHead>
                <TableHead className="text-right">Repeat</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packages.map(p => (
                <TableRow key={p.name}>
                  <TableCell className="font-medium text-sm">{p.name}</TableCell>
                  <TableCell className="text-right">{p.sales}</TableCell>
                  <TableCell className="text-right">{p.revenue.toLocaleString()} ₽</TableCell>
                  <TableCell className="text-right">{p.repeatRate}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// --- LTV ---
function LTVBlock({ ltv, avgHitsPerPayer }: { ltv: DashData["ltv"]; avgHitsPerPayer: number }) {
  const items = [
    { label: "Средний чек", value: `${ltv.avgCheck} ₽` },
    { label: "Хитов / пользователь", value: ltv.avgHitsPerUser },
    { label: "Хитов / платящий", value: avgHitsPerPayer },
    { label: "LTV", value: `${ltv.ltv} ₽` },
    { label: "Повторные покупатели", value: `${ltv.repeatBuyerShare}%` },
  ];
  return (
    <Card className="bg-card border-border">
      <CardHeader><CardTitle>💎 LTV и средний чек</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.label} className="flex justify-between p-2.5 rounded-lg bg-muted/50">
              <span className="text-sm text-muted-foreground">{item.label}</span>
              <span className="font-bold">{item.value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// --- UTM ---
function UTMBlock({ rows }: { rows: DashData["utmRows"] }) {
  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle>📊 Качество трафика по источникам</CardTitle>
        <CardDescription>Conversion = purchase / users</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Нет данных</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>utm_source</TableHead>
                  <TableHead className="text-right">Users</TableHead>
                  <TableHead className="text-right">Demo</TableHead>
                  <TableHead className="text-right">Purchase</TableHead>
                  <TableHead className="text-right">Conv.</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Avg Check</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.source}>
                    <TableCell className="font-medium">{r.source}</TableCell>
                    <TableCell className="text-right">{r.users}</TableCell>
                    <TableCell className="text-right">{r.demos}</TableCell>
                    <TableCell className="text-right">{r.purchases}</TableCell>
                    <TableCell className="text-right">
                      <span className={cn(r.conversion >= 30 ? "text-green-500" : r.conversion >= 15 ? "text-yellow-500" : "text-destructive", "font-medium")}>
                        {r.conversion}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right">{r.revenue.toLocaleString()} ₽</TableCell>
                    <TableCell className="text-right">{r.avgCheck} ₽</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- New User Behavior ---
function NewUserBlock({ data }: { data: DashData["newUserBehavior"] }) {
  const steps = [
    { label: "Новые пользователи", value: data.total, emoji: "👤" },
    { label: "Пробный хит", value: data.usedTrial, emoji: "🎵" },
    { label: "Увидели 99₽", value: data.shownOffer, emoji: "🏷️" },
    { label: "Купили 99₽", value: data.bought99, emoji: "💳" },
    { label: "Повторная", value: data.repeatPurchase, emoji: "🔁" },
  ];
  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" />Новые пользователи</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {steps.map((s, i) => (
            <div key={s.label} className="text-center p-3 rounded-lg bg-muted/50 relative">
              <p className="text-xl font-bold">{s.value}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{s.emoji} {s.label}</p>
              {i > 0 && s.value > 0 && steps[i - 1].value > 0 && (
                <Badge variant="outline" className="absolute -top-2 right-1 text-[10px]">
                  {Math.round((s.value / steps[i - 1].value) * 100)}%
                </Badge>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// --- 10. Party Analysis ---
function PartyBlock({ data }: { data: DashData["partyAnalysis"] }) {
  if (data.totalBuyers === 0) return null;
  return (
    <Card className="bg-card border-border border-purple-500/20">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <PartyPopper className="w-5 h-5 text-purple-500" />
          👥 Пакеты 10+ хитов (компания)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-purple-500/5 text-center">
            <p className="text-xs text-muted-foreground">Покупателей</p>
            <p className="text-xl font-bold">{data.totalBuyers}</p>
          </div>
          <div className="p-3 rounded-lg bg-purple-500/5 text-center">
            <p className="text-xs text-muted-foreground">Ср. хитов создано</p>
            <p className="text-xl font-bold">{data.avgHitsCreated}</p>
          </div>
          <div className="p-3 rounded-lg bg-purple-500/5 text-center">
            <p className="text-xs text-muted-foreground">Создали 8+ хитов</p>
            <p className="text-xl font-bold">{data.pct8plus}%</p>
          </div>
          <div className="p-3 rounded-lg bg-purple-500/5 text-center">
            <p className="text-xs text-muted-foreground">Использовали все</p>
            <p className="text-xl font-bold">{data.pctCompleted}%</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// --- 4. Cohort ---
function CohortBlock({ cohorts }: { cohorts: DashData["cohorts"] }) {
  if (cohorts.length === 0) return null;
  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-primary" />
          📅 Когортный анализ (по неделям)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Неделя</TableHead>
                <TableHead className="text-right">Новые</TableHead>
                <TableHead className="text-right">День 0</TableHead>
                <TableHead className="text-right">День 7</TableHead>
                <TableHead className="text-right">День 30</TableHead>
                <TableHead className="text-right">LTV</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cohorts.map(c => (
                <TableRow key={c.week}>
                  <TableCell className="font-medium">{c.week}</TableCell>
                  <TableCell className="text-right">{c.newUsers}</TableCell>
                  <TableCell className="text-right">{c.purchaseDay0}</TableCell>
                  <TableCell className="text-right">{c.purchaseDay7}</TableCell>
                  <TableCell className="text-right">{c.purchaseDay30}</TableCell>
                  <TableCell className="text-right font-medium">{c.ltv} ₽</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Charts ---
function ChartsBlock({ dailyData }: { dailyData: DashData["dailyData"] }) {
  const revenueConfig = { revenue: { label: "Выручка ₽", color: "hsl(var(--primary))" } };
  const purchasesConfig = { purchases: { label: "Покупки", color: "hsl(var(--primary))" } };
  const conversionConfig = { conversion: { label: "Конверсия %", color: "hsl(var(--destructive))" } };

  return (
    <div className="grid grid-cols-1 gap-6">
      <Card className="bg-card border-border">
        <CardHeader><CardTitle>📈 Выручка по дням</CardTitle></CardHeader>
        <CardContent>
          <ChartContainer config={revenueConfig} className="h-[250px]">
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="date" className="text-xs" />
              <YAxis className="text-xs" />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="revenue" fill="var(--color-revenue)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader><CardTitle>📊 Покупки по дням</CardTitle></CardHeader>
          <CardContent>
            <ChartContainer config={purchasesConfig} className="h-[200px]">
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="date" className="text-xs" />
                <YAxis className="text-xs" />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="purchases" fill="var(--color-purchases)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader><CardTitle>📉 Конверсия демо→покупка</CardTitle></CardHeader>
          <CardContent>
            <ChartContainer config={conversionConfig} className="h-[200px]">
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="date" className="text-xs" />
                <YAxis className="text-xs" unit="%" />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Line type="monotone" dataKey="conversion" stroke="var(--color-conversion)" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// --- Refund Analytics ---
function RefundBlock({ data, isLoading }: { data: ReturnType<typeof useRefundStats>["data"]; isLoading: boolean }) {
  return (
    <Card className="bg-card border-border border-orange-500/20">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <RotateCcw className="w-5 h-5 text-orange-500" />
          🔄 Рефанды и ретраи Suno
        </CardTitle>
        <CardDescription>Возвраты хитов при неудачных генерациях</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="p-3 rounded-lg bg-orange-500/5 text-center">
                <p className="text-xl font-bold">{data?.totalRefunds || 0}</p>
                <p className="text-[10px] text-muted-foreground">Всего рефандов</p>
              </div>
              <div className="p-3 rounded-lg bg-orange-500/5 text-center">
                <p className="text-xl font-bold">{data?.totalRefundedHits || 0}</p>
                <p className="text-[10px] text-muted-foreground">Хитов возвращено</p>
              </div>
              <div className="p-3 rounded-lg bg-orange-500/5 text-center">
                <p className="text-xl font-bold">{data?.autoRefunds || 0}</p>
                <p className="text-[10px] text-muted-foreground">Авто-рефандов</p>
              </div>
              <div className="p-3 rounded-lg bg-orange-500/5 text-center">
                <p className="text-xl font-bold">{data?.totalRetries || 0}</p>
                <p className="text-[10px] text-muted-foreground">Ретраев Suno</p>
              </div>
            </div>

            {data?.refundedGenerations?.length ? (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">История рефандов</p>
                {data.refundedGenerations.map((g) => (
                  <div key={g.id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/50 border border-border/50">
                    <div className="flex items-center gap-2">
                      <RotateCcw className="w-3.5 h-3.5 text-orange-500" />
                      <div>
                        <p className="text-sm font-medium">{g.userName}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {new Date(g.created_at).toLocaleDateString("ru-RU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px]">💎 {g.wow_hits_used} хит</Badge>
                      {g.wasAutoRefunded ? (
                        <Badge className="bg-green-500/10 text-green-500 text-[10px]">✓ Авто</Badge>
                      ) : (
                        <Badge className="bg-orange-500/10 text-orange-500 text-[10px]">Ручной</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4 text-sm">Рефандов пока нет 🎉</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
